import React, { useState } from 'react';
import Textarea from '../ui/Textarea';
import Select from '../ui/Select';
import { BookingData } from '../BookingForm/types';
import { formatDuration } from '../../utils/validation';
import {
  ChevronDown,
  ChevronUp,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  User as UserIcon,
  Briefcase,
  CreditCard,
  FileText,
  DollarSign,
  Building2
} from 'lucide-react';
import { format } from 'date-fns';

export type BookingCardMode = 'readonly' | 'admin' | 'customer' | 'serviceprovider';

export interface BookingCardProps {
  booking: BookingData & {
    id?: string;
    booking_number?: string;
    status?: string;
    created_at?: string;
    cancellation_reason?: string;
    status_reason?: string;
    internal_notes?: string;
    estimated_cost?: number;
    service_provider_name?: string;
  };
  mode: BookingCardMode;
  currentUser?: any | null;
  /**
   * onCancel now expects (bookingId, status, reason) for proper status update
   */
  onConfirm?: (bookingId: string) => void;
  onCancel?: (bookingId: string, status: string, reason: string) => void;
  onEdit?: (bookingId: string) => void;
  onUpdateNotes?: (bookingId: string, notes: string) => void;
  onUpdateStatusReason?: (bookingId: string, reason: string) => void;
  className?: string;
}

/**
 * BookingCard displays booking details and actions based on mode.
 * @param props BookingCardProps
 */
export const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  mode,
  currentUser,
  onConfirm,  onCancel,
  onEdit,
  onUpdateNotes,
  className = '',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // Toggle inline update section for booking actions (single declaration)
  const [showInlineUpdate, setShowInlineUpdate] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [selectedCancellationReason, setSelectedCancellationReason] = useState('');
  const [internalNotes, setInternalNotes] = useState(booking.internal_notes || '');  const [isEditingNotes, setIsEditingNotes] = useState(false);
  
  const toggleExpand = () => setIsExpanded(!isExpanded);
  const handleCancelConfirm = () => {
    // Check if required parameters are available and log helpful debug info
    if (!onCancel) {
      console.error('BookingCard: onCancel prop is not provided');
      alert('Unable to cancel booking: The cancel function is not available. Please refresh the page or contact support.');
      return;
    }
    if (!booking.id) {
      console.error('BookingCard: booking.id is missing or undefined', booking);
      alert('Unable to cancel booking: The booking ID is missing. Please refresh the page or contact support.');
      return;
    }
    if (!selectedCancellationReason) {
      console.error('BookingCard: No cancellation reason selected');
      alert('Please select a cancellation reason to continue.');
      return;
    }
    if (selectedCancellationReason === 'Other' && !cancellationReason) {
      console.error('BookingCard: No custom reason provided for "Other" selection');
      alert('Please provide details for your cancellation reason.');
      return;
    }
    try {
      // Prepare the final reason
      const finalReason = selectedCancellationReason === 'Other' 
        ? cancellationReason 
        : selectedCancellationReason;
      // Call onCancel with status 'cancelled' and the reason
      onCancel(booking.id, 'cancelled', finalReason);
    } catch (error) {
      console.error('BookingCard: Error during cancellation confirmation:', error);
      alert('An error occurred while cancelling the booking. Please try again.');
    } finally {
      setCancellationReason(''); // Clear reason after submission
      setSelectedCancellationReason(''); // Clear selected reason
    }
  };

  const handleSaveNotes = () => {
    if (onUpdateNotes && booking.id) {
      console.log('BookingCard: Saving internal notes:', { bookingId: booking.id, notes: internalNotes });
      onUpdateNotes(booking.id, internalNotes);
    }
    setIsEditingNotes(false);
  };
    // Status reason editor has been removed
  const statusColor = (status?: string) => {
    switch (status) {
      case 'pending':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'pending_confirmation':
        return 'bg-info/10 text-info border-info/20';
      case 'confirmed':
        return 'bg-success/10 text-success border-success/20';
      case 'cancelled':
        return 'bg-destructive/10 text-destructive border-destructive/20';
      case 'completed':
        return 'bg-accent/10 text-accent border-accent/20';
      default: 
        return 'bg-muted text-foreground-muted border-border';
    }
  };

  // Available cancellation reasons
  const cancellationReasons = [
    'Customer request',
    'Service unavailable',
    'Double booking',
    'Weather conditions',
    'Vehicle breakdown',
    'Other'
  ];
  // Permissions and actions by mode
  const canEdit = mode === 'customer' && booking.status === 'pending';
  const canCancel = (mode === 'customer' && ['pending', 'pending_confirmation', 'confirmed'].includes(booking.status || '')) ||
                    ((mode === 'admin' || mode === 'serviceprovider') && ['pending', 'pending_confirmation', 'confirmed'].includes(booking.status || ''));

  const canConfirm = ((mode === 'admin' || mode === 'serviceprovider') && ['pending', 'pending_confirmation'].includes(booking.status || ''));
  // Status reason editor has been removed

  return (    <div
      className={`card-container ${className}`}
      tabIndex={0}
      aria-label={`Booking card for booking #${booking.booking_number || booking.id}`}
      data-testid={`booking-card-${booking.booking_number || booking.id}`}
    >

      {/* Header */}
      <div className="flex items-center justify-between border-b border-border pb-2">
        <div>
          <div className="font-semibold text-lg text-primary" data-testid="booking-card-booking-number">
            Booking #{booking.booking_number || booking.id}
          </div>
          {booking.service_provider_name && (
            <div className="flex items-center gap-1 text-sm text-foreground-muted" data-testid="booking-card-service-provider">
              <Building2 size={14} />
              <span>{booking.service_provider_name}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-foreground-muted hidden sm:block" data-testid="booking-card-created-date">
            <span className="font-semibold">Submitted:</span> {booking.created_at ? format(new Date(booking.created_at), 'EEEE, d MMMM yyyy, h:mm a') : 'N/A'}
          </div>
          <button
            onClick={toggleExpand}
            className="p-1 rounded-full hover:bg-accent"
            aria-label={isExpanded ? "Collapse details" : "Expand details"}
            data-testid="booking-card-expand-toggle"
          >
            {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>

      {/* Status and mobile timestamp */}
      <div className="flex justify-between items-center sm:hidden text-xs text-foreground-muted">
        <div>
          <span className="font-semibold">Submitted:</span> {booking.created_at ? new Date(booking.created_at).toLocaleString() : 'N/A'}
        </div>
        <div>
          <span className={`capitalize inline-block px-2 py-1 rounded border text-xs font-semibold ${statusColor(booking.status)}`} data-testid="booking-card-status-badge">
            {booking.status}
          </span>
        </div>
      </div>
      
      {/* Essential Details - Always Visible */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="booking-card-essential-details">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <UserIcon size={16} className="text-primary" />
            <div data-testid="booking-card-passenger-name"><span className="font-semibold">Name:</span> {booking.passengerName}</div>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-primary" />
            <div className="truncate" data-testid="booking-card-pickup-location"><span className="font-semibold">Pickup:</span> {booking.pickup}</div>
          </div>
          <div className="flex items-center gap-2">
            <MapPin size={16} className="text-primary rotate-180" />
            <div className="truncate" data-testid="booking-card-dropoff-location"><span className="font-semibold">Dropoff:</span> {booking.destination}</div>
          </div>
          {/* Show status reason if present */}
          {booking.status_reason && (
            <div className="flex items-center gap-2">
              <FileText size={16} className="text-primary" />
              <div><span className="font-semibold">Status Reason:</span> {booking.status_reason}</div>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Calendar size={16} className="text-primary" />
            <div>
              <span className="font-semibold">Requested:</span>
              {booking.pickupDate && (
                <span className="ml-2 text-xs text-foreground-muted">
                  {format(new Date(booking.pickupDate + 'T00:00:00'), 'EEEE, d MMMM yyyy')}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Clock size={16} className="text-primary" />
            <div>
              <span className="font-semibold">Pickup Time:</span>{' '}
              {booking.pickupTime}
              {booking.pickupType === 'asap' && (
                <span className="ml-1 inline-block px-2 py-0.5 rounded bg-warning/10 text-warning text-xs font-semibold">(ASAP)</span>
              )}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">            <div>
              <span className="font-semibold mr-2">Status:</span>
              <span className={`capitalize inline-block px-2 py-1 rounded border text-xs font-semibold ${statusColor(booking.status)}`}>
                {booking.status}
              </span>
            </div>
          </div>
        </div>
      </div>
        {/* Expanded Details */}
      {isExpanded && (
        <div className="space-y-4 mt-2 pt-4 border-t border-border">
          {/* Passenger Details Section - Show for all modes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <h3 className="font-semibold text-foreground text-sm uppercase tracking-wider">Passenger Details</h3>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserIcon size={16} className="text-primary" />
                  <div><span className="font-semibold">Name:</span> {booking.passengerName}</div>
                </div>
                
                {booking.companyName && (
                  <div className="flex items-center gap-2">
                    <Briefcase size={16} className="text-primary" />
                    <div><span className="font-semibold">Company:</span> {booking.companyName}</div>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Phone size={16} className="text-primary" />
                  <div><span className="font-semibold">Phone:</span> {booking.countryCode} {booking.passengerMobile}</div>
                </div>
                
                {booking.passengerEmail && (
                  <div className="flex items-center gap-2">
                    <Mail size={16} className="text-primary" />
                    <div className="truncate"><span className="font-semibold">Email:</span> {booking.passengerEmail}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="space-y-3">
              <h3 className="font-semibold text-foreground text-sm uppercase tracking-wider">Booking Details</h3>
              
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <UserIcon size={16} className="text-primary" />
                  <div><span className="font-semibold">Passengers:</span> {booking.passengers !== undefined ? booking.passengers : 'N/A'}</div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Briefcase size={16} className="text-primary" />
                  <div><span className="font-semibold">Luggage:</span> {booking.luggage !== undefined ? `${booking.luggage} piece(s)` : 'N/A'}</div>
                </div>
                
                <div className="flex items-center gap-2">
                  <CreditCard size={16} className="text-primary" />
                  <div><span className="font-semibold">Payment:</span> {booking.paymentMethod || 'N/A'}</div>
                </div>
                
                {booking.estimatedDuration && (
                  <div className="flex items-center gap-2">
                    <Clock size={16} className="text-primary" />
                    <div>
                      <span className="font-semibold">Duration:</span> 
                      {typeof booking.estimatedDuration === 'number' 
                        ? formatDuration(booking.estimatedDuration) 
                        : booking.estimatedDuration?.car 
                          ? formatDuration(booking.estimatedDuration.car) 
                          : 'N/A'
                      }
                    </div>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <DollarSign size={16} className="text-primary" />
                  <div>
                    <span className="font-semibold">Est. Cost:</span> 
                    ${typeof booking.estimated_cost === 'number' ? booking.estimated_cost.toFixed(2) : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>
            {/* Status Information - For Cancelled Bookings Only */}          {booking.status === 'cancelled' && booking.status_reason && (
            <div className="space-y-2 pt-2">
              <h3 className="font-semibold text-foreground text-sm uppercase tracking-wider">Status Information</h3>
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <span className="font-semibold">Cancellation Reason:</span> {booking.status_reason}
              </div>
            </div>
          )}
          
          {/* Internal Notes - Admin/ServiceProvider Only */}
          {(['admin', 'serviceprovider'].includes(mode)) && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2">
                <FileText size={16} className="text-primary" />
                <h3 className="font-semibold text-foreground text-sm uppercase tracking-wider">Internal Notes</h3>
              </div>
              
              <div className="p-3 bg-background-paper rounded-lg border border-border">
                {isEditingNotes ? (
                  <div className="space-y-2">
                    <Textarea
                      value={internalNotes}
                      onChange={e => setInternalNotes(e.target.value)}
                      className="w-full p-2 border border-border rounded-md bg-input text-foreground"
                      placeholder="Add internal notes here (not visible to customer)"
                      rows={3}
                    />
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => setIsEditingNotes(false)}
                        className="px-3 py-1 bg-secondary text-secondary-foreground rounded"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleSaveNotes}
                        className="px-3 py-1 bg-primary text-primary-foreground rounded"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-start">
                    <div className="text-foreground-muted">
                      {internalNotes || 'No internal notes'}
                    </div>
                    <button 
                      onClick={() => setIsEditingNotes(true)}
                      className="text-primary hover:text-primary/80 text-sm"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Fee Field - Admin/ServiceProvider Only */}
          {(['admin', 'serviceprovider'].includes(mode)) && (
            <div className="space-y-2 pt-2">
              <div className="flex items-center gap-2">
                <DollarSign size={16} className="text-primary" />
                <h3 className="font-semibold text-foreground text-sm uppercase tracking-wider">Service Fee</h3>
              </div>
              
              <div className="p-3 bg-background-paper rounded-lg border border-border">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="form-input w-full p-2 border border-border rounded-md bg-input text-foreground"
                    placeholder="Enter service fee"
                    min="0"
                    step="0.01"
                  />
                  <button 
                    className="px-3 py-2 bg-primary text-primary-foreground rounded"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Actions */}
      <div className="flex flex-wrap gap-2 mt-2 pt-2 border-t border-border" data-testid="booking-card-actions">        {canEdit && (
          <button
            className="bg-primary hover:bg-primary/90 text-primary-foreground px-4 py-2 rounded-lg transition-colors flex-1 sm:flex-none"
            onClick={() => onEdit && booking.id && onEdit(booking.id)}
            data-testid="booking-card-edit-button"
          >
            Edit
          </button>
        )}
        {canCancel && (
          <button
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground px-4 py-2 rounded-lg transition-colors flex-1 sm:flex-none"
            onClick={() => {
              // reset inputs when opening update section
              setSelectedCancellationReason('');
              setCancellationReason('');              setIsEditingNotes(false);
              setShowInlineUpdate(prev => !prev);
            }}
            data-testid="booking-card-manage-button"
          >
            {showInlineUpdate ? 'Close Update' : 'Manage'}
          </button>
        )}

        {canConfirm && (
          <button
            className="bg-success hover:bg-success/90 text-success-foreground px-4 py-2 rounded-lg transition-colors flex-1 sm:flex-none"
            onClick={() => onConfirm && booking.id && onConfirm(booking.id)}
            data-testid="booking-card-confirm-button"
          >
            Confirm
          </button>
        )}

        {!isExpanded && (
          <button
            className="ml-auto bg-secondary hover:bg-secondary/80 text-secondary-foreground px-4 py-2 rounded-lg transition-colors"
            onClick={toggleExpand}
            data-testid="booking-card-view-details-button"
          >
            View Details
          </button>
        )}
      </div>
        {/* Inline Manage Section (mode-dependent) */}
      {showInlineUpdate && (        <div className="p-4 border border-info/20 rounded-lg bg-info/10 space-y-4 mt-2" data-testid="booking-card-inline-update-section">
          {/* Cancellation Section - Common for all modes */}
          <h3 className="text-lg font-semibold text-info-foreground">
            {mode === 'customer' ? 'Cancel Booking' : 'Update Booking'}
          </h3>

          {/* Cancellation Reason - For all modes */}
          <div className="space-y-2">
            <label className="block">
              <span className="text-sm font-medium text-info-foreground">Cancellation Reason</span>
              <Select
                value={selectedCancellationReason}
                onChange={val => setSelectedCancellationReason(val as string)}
                options={[
                  { value: '', label: 'Select reason', disabled: true },
                  ...cancellationReasons.map(reason => ({ value: reason, label: reason }))
                ]}                selectClassName="mt-1 block w-full p-2 border border-border rounded bg-input text-foreground"
                placeholder="Select reason"
                data-testid="booking-card-cancellation-reason-select"
              />
            </label>
            {selectedCancellationReason === 'Other' && (
              <label className="block">
                <span className="text-sm font-medium text-info-foreground">Other Reason</span>
                <Textarea
                  value={cancellationReason}
                  onChange={e => setCancellationReason(e.target.value)}
                  className="mt-1 block w-full p-2 border border-border rounded bg-input text-foreground"
                  rows={3}
                  data-testid="booking-card-custom-reason-textarea"
                />
              </label>
            )}
          </div>
          
          {/* Admin/ServiceProvider Additional Options */}
          {(['admin', 'serviceprovider'].includes(mode)) && (
            <>              {/* Border line between sections */}
              <div className="mb-4 mt-6 pt-4 border-t border-info/20"></div>
              
              {/* Internal Notes Editor */}
              <div>
                <label className="font-semibold mb-1 block text-info-foreground">Internal Notes:</label>
                {isEditingNotes ? (
                  <div className="space-y-2">                    <Textarea
                      value={internalNotes}
                      onChange={e => setInternalNotes(e.target.value)}
                      className="w-full p-2 border border-border rounded-md bg-input text-foreground"
                      placeholder="Add internal notes here (not visible to customer)"
                      rows={3}
                    />
                    <div className="flex justify-end gap-2">
                      <button 
                        onClick={() => setIsEditingNotes(false)}
                        className="px-3 py-1 bg-secondary text-secondary-foreground rounded"
                      >
                        Cancel
                      </button>
                      <button 
                        onClick={handleSaveNotes}
                        className="px-3 py-1 bg-primary text-primary-foreground rounded"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (                  <div className="flex justify-between items-start">
                    <div className="text-foreground-muted">
                      {internalNotes || 'No internal notes'}
                    </div>
                    <button 
                      onClick={() => setIsEditingNotes(true)}
                      className="text-primary hover:text-primary/80 text-sm"
                    >
                      Edit
                    </button>
                  </div>
                )}
              </div>
                {/* Fee Field */}
              <div className="mt-4">
                <label className="font-semibold mb-1 block text-info-foreground">Service Fee:</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    className="form-input w-full p-2 border border-border rounded-md bg-input text-foreground"
                    placeholder="Enter service fee"
                    min="0"
                    step="0.01"
                  />
                  <button 
                    className="px-3 py-2 bg-primary text-primary-foreground rounded"
                  >
                    Apply
                  </button>
                </div>
              </div>
            </>
          )}
          
          {/* Action Buttons */}
          <div className="flex space-x-2 justify-end mt-4">
            {canConfirm && ['admin', 'serviceprovider'].includes(mode) && (              <button
                onClick={() => onConfirm && booking.id && onConfirm(booking.id)}
                className="bg-success text-success-foreground py-2 px-4 rounded hover:bg-success/90"
              >
                Confirm Booking
              </button>
            )}              <button
              onClick={handleCancelConfirm}
              disabled={!selectedCancellationReason || (selectedCancellationReason === 'Other' && !cancellationReason)}
              className="bg-destructive text-destructive-foreground py-2 px-4 rounded hover:bg-destructive/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Cancel
            </button>
            
            <button
              onClick={() => setShowInlineUpdate(false)}
              className="border border-border py-2 px-4 rounded hover:bg-secondary text-foreground"
            >
              Close
            </button>
          </div>        </div>
      )}
    </div>
  );
};

export default BookingCard;
